/*
 * CW accessibility fixes (Accessible360 maintenance audit, Sept 2026).
 * Behaviour only: names, states and focus. Loaded by snippets/cw-a11y.liquid.
 *   #17   Search returns focus to the button that opened it
 *   #370  Mobile menu moves focus to its Close button, and back to the menu button on close
 *   #333  "World of Wow" dropdown closes when keyboard focus leaves it
 *   #341 #369  In-page links move focus to their target (the theme's smooth scroll cancelled the jump)
 *   #127  Product gallery: only the visible slide is exposed to screen readers and the keyboard
 *   #397 #398  Size list: focus moves into the list when it opens; the selected size has aria-current
 *   #395 #396  Skio subscribe widget: legend for the purchase options, label for the delivery dropdown
 *   #337  Allyant badge: real image with alt text instead of a CSS background
 */
(function () {
  'use strict';

  var d = document;
  var FOCUSABLE = 'a[href], area[href], button, input, select, textarea, iframe, [tabindex], [contenteditable="true"]';

  function ready(fn) {
    if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  function onAttr(el, attr, cb) {
    if (!el) return;
    new MutationObserver(function () { cb(el.getAttribute(attr)); }).observe(el, { attributes: true, attributeFilter: [attr] });
  }

  // Elements can't take focus while still hidden by a CSS transition, so keep trying briefly.
  function focusSoon(el) {
    if (!el) return;
    var tries = 0;
    (function attempt() {
      el.focus({ preventScroll: true });
      if (d.activeElement !== el && tries++ < 60) setTimeout(attempt, 50);
    })();
  }

  // Hide or restore a subtree for keyboard users (screen readers are handled by aria-hidden on the wrapper).
  function setInert(container, hidden) {
    container.querySelectorAll(FOCUSABLE).forEach(function (el) {
      if (hidden) {
        if (!el.hasAttribute('data-cw-tabindex')) el.setAttribute('data-cw-tabindex', el.getAttribute('tabindex') || '');
        el.setAttribute('tabindex', '-1');
      } else if (el.hasAttribute('data-cw-tabindex')) {
        var prev = el.getAttribute('data-cw-tabindex');
        if (prev === '') el.removeAttribute('tabindex'); else el.setAttribute('tabindex', prev);
        el.removeAttribute('data-cw-tabindex');
      }
    });
  }

  ready(function () {
    // #17 Search
    var search = d.getElementById('Search');
    if (search) {
      var searchOpener = null;
      d.addEventListener('click', function (e) {
        var t = e.target.closest && e.target.closest('[data-action="toggle-search"]');
        if (t && !search.contains(t)) searchOpener = t;
      }, true);
      onAttr(search, 'aria-hidden', function (v) {
        if (v !== 'true' || !searchOpener) return;
        var opener = searchOpener;
        searchOpener = null;
        if (search.contains(d.activeElement) || d.activeElement === d.body) focusSoon(opener);
      });
    }

    // #370 Mobile menu drawer
    var menu = d.getElementById('sidebar-menu');
    if (menu) {
      var menuOpener = null;
      d.addEventListener('click', function (e) {
        var t = e.target.closest && e.target.closest('[data-action="open-drawer"][data-drawer-id="sidebar-menu"]');
        if (t) menuOpener = t;
      }, true);
      onAttr(menu, 'aria-hidden', function (v) {
        if (v === 'false') {
          var close = menu.querySelector('.Drawer__Close');
          focusSoon(close);
          // Slow devices: try again once the slide-in transition has finished
          menu.addEventListener('transitionend', function onEnd() {
            menu.removeEventListener('transitionend', onEnd);
            if (close && menu.getAttribute('aria-hidden') === 'false' && !menu.contains(d.activeElement)) close.focus({ preventScroll: true });
          });
        } else if (v === 'true' && menuOpener) {
          var opener = menuOpener;
          menuOpener = null;
          if (menu.contains(d.activeElement) || d.activeElement === d.body) focusSoon(opener);
        }
      });
    }

    // #333 Desktop dropdown menus opened by a button: close when focus leaves the menu item
    d.querySelectorAll('.Header__MainNav button.mainNavItem[aria-controls]').forEach(function (btn) {
      var panel = d.getElementById(btn.getAttribute('aria-controls'));
      if (!panel || !panel.classList.contains('DropdownMenu')) return;
      var item = btn.closest('.HorizontalList__Item');
      item.addEventListener('focusout', function (e) {
        if (e.relatedTarget && item.contains(e.relatedTarget)) return;
        if (btn.getAttribute('aria-expanded') === 'true') btn.click();
      });
    });

    // #127 Product gallery
    d.querySelectorAll('.Product__Slideshow').forEach(function (show) {
      var cells = Array.prototype.slice.call(show.querySelectorAll('.Product__SlideItem'));
      if (cells.length < 2) return;
      function sync() {
        if (!cells.some(function (c) { return c.classList.contains('is-selected'); })) return;
        cells.forEach(function (c) {
          var on = c.classList.contains('is-selected');
          c.setAttribute('aria-hidden', on ? 'false' : 'true');
          c.setAttribute('tabindex', on ? '0' : '-1');
          setInert(c, !on);
        });
      }
      cells.forEach(function (c) {
        new MutationObserver(sync).observe(c, { attributes: true, attributeFilter: ['class'] });
      });
      sync();
    });

    // #397 #398 Size list
    d.querySelectorAll('.OptionSelector').forEach(function (pop) {
      var values = pop.querySelectorAll('.Popover__Value');
      function sync() {
        values.forEach(function (b) {
          if (b.classList.contains('is-selected')) b.setAttribute('aria-current', 'true');
          else b.removeAttribute('aria-current');
        });
      }
      values.forEach(function (b) {
        new MutationObserver(sync).observe(b, { attributes: true, attributeFilter: ['class'] });
      });
      sync();
      onAttr(pop, 'aria-hidden', function (v) {
        if (v === 'false') focusSoon(pop.querySelector('.Popover__Value.is-selected') || values[0]);
      });
    });

    // #337 Allyant badge
    var badgeLink = d.querySelector('.Footer__Block--links:last-child .Linklist__Item:last-child > a');
    if (badgeLink && !d.querySelector('.cw-allyant-badge')) {
      var m = (getComputedStyle(badgeLink).backgroundImage || '').match(/url\(["']?([^"')]+)["']?\)/);
      if (m && /allyant/i.test(m[1])) {
        var img = d.createElement('img');
        img.src = m[1];
        img.alt = 'Reviewed by Allyant for accessibility';
        img.className = 'cw-allyant-badge';
        img.height = 85;
        badgeLink.parentElement.appendChild(img);
        badgeLink.parentElement.classList.add('cw-has-badge');
      }
    }

    // #395 #396 Skio subscribe widget (app block; Skio may re-render, so re-apply on change)
    function labelSkio(fs) {
      if (!fs.querySelector(':scope > legend')) {
        var lg = d.createElement('legend');
        lg.className = 'sr-only';
        lg.textContent = 'Purchase options';
        fs.insertBefore(lg, fs.firstChild);
      }
      fs.querySelectorAll('select').forEach(function (s) {
        if (!s.getAttribute('aria-label') && !(s.labels && s.labels.length)) s.setAttribute('aria-label', 'Delivery frequency');
      });
    }
    d.querySelectorAll('fieldset.skio-plan-picker').forEach(function (fs) {
      labelSkio(fs);
      new MutationObserver(function () { labelSkio(fs); }).observe(fs, { childList: true, subtree: true });
    });
  });

  // #341 #369 In-page links. The theme scrolls smoothly and cancels the jump; move focus to the target as well.
  d.addEventListener('click', function (e) {
    if (!e.defaultPrevented) return;
    var a = e.target.closest && e.target.closest('a[href^="#"]');
    if (!a || a.hasAttribute('data-focus-on-click') || a.hasAttribute('data-action') || a.hasAttribute('role') || a.hasAttribute('aria-controls')) return;
    var id = a.getAttribute('href').slice(1);
    if (!id || id === 'main' || id === '0') return;
    var target;
    try { target = d.getElementById(decodeURIComponent(id)); } catch (err) { return; }
    if (!target) return;
    if (!target.matches(FOCUSABLE)) target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
    // The theme's smooth scroll gets cancelled part way (the PDP "FAQ" link stops ~3,000px short), so jump
    // to the target like a normal anchor link, leaving room for the sticky header.
    setTimeout(function () {
      var header = d.getElementById('shopify-section-header');
      var offset = 10;
      if (header && /sticky|fixed/.test(getComputedStyle(header).position)) offset += header.getBoundingClientRect().height;
      window.scrollTo({ top: target.getBoundingClientRect().top + window.pageYOffset - offset, behavior: 'auto' });
    }, 50);
  });
})();
